import { Loader2, XCircle } from 'lucide-react'
import type { CancelReasonOption } from 'modules/permit/api/permitService'
import { Button } from 'shared/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from 'shared/ui/dialog'
import { Label } from 'shared/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'shared/ui/select'
import { Textarea } from 'shared/ui/textarea'

interface CancelDialogPermitSummary {
  permitId: string
  permitNumber: string
}

interface OpenPermitCancelDialogProps {
  open: boolean
  permitIds: string[]
  permits: CancelDialogPermitSummary[]
  cancelReason: string
  cancelReasonDetail: string
  cancelReasons: CancelReasonOption[]
  isSubmitting: boolean
  onOpenChange: (open: boolean) => void
  onCancelReasonChange: (value: string) => void
  onCancelReasonDetailChange: (value: string) => void
  onSubmit: () => void
}

export function OpenPermitCancelDialog({
  open,
  permitIds,
  permits,
  cancelReason,
  cancelReasonDetail,
  cancelReasons,
  isSubmitting,
  onOpenChange,
  onCancelReasonChange,
  onCancelReasonDetailChange,
  onSubmit,
}: OpenPermitCancelDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><XCircle className="h-5 w-5 text-red-500" />{permitIds.length > 1 ? 'Mass Cancel Permit' : 'ยกเลิก Permit'}</DialogTitle>
          <DialogDescription>{permitIds.length > 1 ? `ยืนยันการยกเลิก Permit จำนวน ${permitIds.length} รายการ` : 'ระบุเหตุผลในการยกเลิก Permit'}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="rounded-lg bg-slate-50 p-4 text-sm text-slate-600">
            <p className="font-medium text-slate-900">รายการที่เลือก {permitIds.length} รายการ</p>
            <ul className="mt-2 space-y-1">
              {permits.slice(0, 5).map((permit) => <li key={permit.permitId}>{permit.permitNumber || permit.permitId}</li>)}
              {permits.length > 5 ? <li>และอีก {permits.length - 5} รายการ</li> : null}
            </ul>
          </div>

          <div className="space-y-2">
            <Label>เหตุผลในการยกเลิก *</Label>
            <Select value={cancelReason} onValueChange={onCancelReasonChange}>
              <SelectTrigger><SelectValue placeholder="เลือกเหตุผล" /></SelectTrigger>
              <SelectContent>
                {cancelReasons.map((reason) => (
                  <SelectItem key={reason.code} value={reason.code}>{reason.description}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="cancel-detail">รายละเอียดเพิ่มเติม</Label>
            <Textarea id="cancel-detail" value={cancelReasonDetail} onChange={(event) => onCancelReasonDetailChange(event.target.value)} placeholder="ระบุรายละเอียดเพิ่มเติม (ถ้ามี)" rows={4} />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>ปิด</Button>
          <Button variant="destructive" onClick={onSubmit} disabled={isSubmitting || !cancelReason}>{isSubmitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <XCircle className="mr-2 h-4 w-4" />}ยืนยันยกเลิก</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
